import { FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  NoteAdd as BlankIcon,
  ContentCopy as DuplicateIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { InputText, useModal, useToastr } from '@farmshare/ui-components';
import { removeTypename } from '@farmshare/utils';
import type { Cut, Extra, Primal, Specification, Trim } from '../types';
import {
  type CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  type CutsheetQuestionnaire as GqlCutsheetQuestionnaire,
  useCutsheetAssignmentRuleSetByCapabilityIdQuery,
  useCutsheetAssignmentRuleSetUpsertMutation,
  useCutsheetCapabilityByIdQuery,
  useCutsheetQuestionnaireDeleteMutation,
  useCutsheetQuestionnaireManyLazyQuery,
  useCutsheetQuestionnaireUpsertMutation,
} from 'lib/graphql';
import { useUserState } from 'providers/user-state-provider';
import {
  useCapabilityHandlers,
  useCatalogState,
  useQuestionHandlers,
  useQuestionnaireState,
} from '../hooks';
import {
  CapabilitiesPanel,
  QuestionnairePanel,
  QuestionnaireWizard,
} from '../components';
import {
  EMPTY_RULE,
  type LocalRule,
  RuleDialog,
} from '../components/assignment-rules';

interface CatalogAndFormsBuilderProps {
  vendorId: string;
  capabilityId: string;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

const statusLabel = (status?: string | null) =>
  STATUS_LABEL[status ?? 'draft'] ?? 'Draft';
const statusColor = (status?: string | null) =>
  STATUS_COLOR[status ?? 'draft'] ?? 'default';

/**
 * Unified Catalog + Form Builder
 *
 * Single-page view that combines:
 * - Left column: Editable catalog (groups, cuts, trim, specs)
 * - Right column: Form builder with a dropdown to switch between forms
 *
 * Replaces the separate Catalog tab and Cut Sheet Forms tab.
 * @param root0
 * @param root0.vendorId
 * @param root0.capabilityId
 */
export const CatalogAndFormsBuilder: FC<CatalogAndFormsBuilderProps> = ({
  vendorId,
  capabilityId,
}) => {
  const { push } = useToastr();
  const { save, ask } = useModal();

  // ----- Catalog state (left side) -----
  const {
    catalog: capabilities,
    addCut,
    updateCut,
    addTrim,
    updateTrim,
    addExtra,
    updateExtra,
    addPrimal,
    updatePrimal,
    removePrimal,
    reorderPrimals,
    addSpecification,
    updateSpecification,
    removeSpecification,
    removeCut,
    removeTrim,
    removeExtra,
    updateCatalogInfo: updateCapabilitiesInfo,
  } = useCatalogState({ vendorId, capabilityId });

  // ----- Form list (for the dropdown) -----
  const [includeArchived, setIncludeArchived] = useState(false);
  const [formListQuery, formListResult] = useCutsheetQuestionnaireManyLazyQuery(
    { fetchPolicy: 'no-cache' },
  );

  useEffect(() => {
    if (capabilityId) {
      formListQuery({ variables: { capabilityId, includeArchived } });
    }
  }, [capabilityId, includeArchived]);

  const forms = useMemo(
    () =>
      (formListResult.data?.cutsheetQuestionnairesByVendorAndAnimal?.filter(
        Boolean,
      ) ?? []) as GqlCutsheetQuestionnaire[],
    [formListResult.data],
  );

  // ----- Selected form (URL-synced via ?formId=) -----
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFormId = searchParams.get('formId');

  const setSelectedFormId = (
    id: string | null,
    opts: { replace?: boolean } = {},
  ) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('formId', id);
    else next.delete('formId');
    setSearchParams(next, opts);
  };

  // Memoized lookup of the selected form in the loaded list. Using a memo
  // (rather than computing inline in the Autocomplete's `value` prop) keeps
  // the reference stable across unrelated re-renders and lets the value
  // update cleanly once the form list finishes loading.
  const selectedForm = useMemo(
    () => forms.find((f) => f._id === selectedFormId) ?? null,
    [forms, selectedFormId],
  );

  // Auto-select first form once the list has actually loaded. Wait for the
  // lazy query to finish so a formId from the URL isn't wiped before forms arrive.
  const formListLoaded =
    formListResult.called && !formListResult.loading && !!formListResult.data;
  useEffect(() => {
    if (!formListLoaded) return;
    if (forms.length === 0) {
      if (selectedFormId) setSelectedFormId(null, { replace: true });
      return;
    }
    if (selectedFormId && forms.some((f) => f._id === selectedFormId)) return;
    // URL-provided formId isn't in the active list — it may be archived.
    // Flip includeArchived on and let the refetch bring it in, rather than
    // silently replacing the user's current selection with forms[0].
    if (selectedFormId && !includeArchived) {
      setIncludeArchived(true);
      return;
    }
    setSelectedFormId(forms[0]._id, { replace: true });
  }, [formListLoaded, forms, selectedFormId, includeArchived]);

  // ----- Form mutations -----
  const [createForm] = useCutsheetQuestionnaireUpsertMutation();
  // `cutsheetQuestionnaireDelete` is a soft-delete — it sets status='archived'.
  const [archiveFormMutation] = useCutsheetQuestionnaireDeleteMutation();

  const handleCreateForm = async () => {
    try {
      const result = await createForm({
        variables: {
          capabilityId,
          name: 'New Cut Sheet Form',
          description: '',
          questions: [],
          status: 'draft',
        },
      });

      const newId = result.data?.cutsheetQuestionnaireUpsert?._id;
      if (newId) {
        await formListResult.refetch?.();
        setSelectedFormId(newId);
        push({
          title: 'Success',
          body: 'Cut sheet form created',
          bg: 'success',
        });
      }
    } catch {
      push({
        title: 'Error',
        body: 'Failed to create cut sheet form',
        bg: 'error',
      });
    }
  };

  const handleArchiveForm = async () => {
    if (!selectedFormId) return;
    try {
      await archiveFormMutation({ variables: { id: selectedFormId } });
      setSelectedFormId(null);
      await formListResult.refetch?.();
      push({ title: 'Archived', body: 'Form archived', bg: 'success' });
    } catch {
      push({ title: 'Error', body: 'Failed to archive form', bg: 'error' });
    }
  };

  const handleDuplicateForm = async () => {
    if (!questionnaire) return;
    try {
      // Deep-clone questions, stripping __typename and IDs so the server
      // generates fresh ones for the copy. ChoiceGroup options also carry
      // client-side ids that CutsheetChoiceOptionInput rejects, so strip
      // those too.
      const cleanedQuestions = removeTypename(
        (questionnaire.questions ?? []).map((q: any) => {
          const { id, _id, options, ...rest } = q;
          if (Array.isArray(options)) {
            return {
              ...rest,
              options: options.map((opt: any) => {
                const { id: _optId, _id: _optUnderscoreId, ...optRest } = opt;
                return optRest;
              }),
            };
          }
          return rest;
        }),
      );

      const result = await createForm({
        variables: {
          capabilityId,
          name: `${questionnaire.name} (Copy)`,
          description: questionnaire.description || '',
          questions: cleanedQuestions,
          status: 'draft',
        },
      });
      const newId = result.data?.cutsheetQuestionnaireUpsert?._id;
      if (newId) {
        await formListResult.refetch?.();
        setSelectedFormId(newId);
        push({ title: 'Success', body: 'Form duplicated', bg: 'success' });
      }
    } catch {
      push({ title: 'Error', body: 'Failed to duplicate form', bg: 'error' });
    }
  };

  // ----- Questionnaire state (right side — for the selected form) -----
  const {
    questionnaire,
    loading: questionnaireLoading,
    addQuestionAtIndex,
    updateQuestion,
    removeQuestion,
    addItemsToQuestion,
    removeItemFromQuestion,
    reorderItemsInQuestion,
    updateQuestionnaireInfo,
    reorderQuestionsByIndex,
    saveQuestionnaire,
    isDirty,
    isSaving,
  } = useQuestionnaireState({
    questionnaireId: selectedFormId || undefined,
    capabilityId,
  });

  // ----- Fetch capability for print settings -----
  const { data: capabilityData } = useCutsheetCapabilityByIdQuery({
    variables: { id: capabilityId },
    skip: !capabilityId,
  });

  // ----- Publish-and-rule dialog state (draft → publish flow) -----
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const { vendor } = useUserState();
  const { data: assignmentRuleSetData, refetch: refetchAssignmentRuleSet } =
    useCutsheetAssignmentRuleSetByCapabilityIdQuery({
      variables: { capabilityId },
      skip: !capabilityId,
    });
  const [upsertAssignmentRuleSet] =
    useCutsheetAssignmentRuleSetUpsertMutation();

  const isPublished = questionnaire?.status === 'published';

  // ----- DnD state -----
  const [isDraggingCapability, setIsDraggingCapability] = useState(false);
  const [activeItem, setActiveItem] = useState<{
    item: Cut | Trim | Extra | Specification;
    type: 'cut' | 'trim' | 'extra' | 'specification';
  } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [newMenuAnchor, setNewMenuAnchor] = useState<null | HTMLElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // ----- Add group handler -----
  const handleAddGroup = () => {
    save({
      type: 'save',
      title: 'Add Group',
      initialValues: { title: '' },
      body: <InputText label="Name" nameOverride="title" required />,
      onSubmit: async (values) => {
        try {
          await addPrimal({ title: values.title as string });
          push({
            title: 'Success',
            body: 'Group added',
            bg: 'success',
          });
        } catch (err) {
          push({
            title: 'Error',
            body: 'Failed to add group',
            bg: 'error',
          });
        }
      },
    });
  };

  const handleEditGroup = (primal: Primal) => {
    const hasCuts = capabilities.cuts.some((c) => c.groupId === primal.id);
    save({
      type: 'save',
      title: 'Edit Group',
      initialValues: { title: primal.title },
      body: (
        <Stack spacing={2}>
          <InputText label="Name" nameOverride="title" required />
          {!hasCuts && (
            <Button
              color="error"
              size="small"
              variant="outlined"
              onClick={() => {
                ask({
                  type: 'ask',
                  title: 'Delete Group?',
                  body: `Are you sure you want to delete "${primal.title}"? This cannot be undone.`,
                  onConfirm: async () => {
                    await removePrimal(primal.id);
                    push({
                      title: 'Success',
                      body: `"${primal.title}" deleted`,
                      bg: 'success',
                    });
                  },
                });
              }}
            >
              Delete Group
            </Button>
          )}
        </Stack>
      ),
      onSubmit: async (values) => {
        await updatePrimal(primal.id, { title: values.title as string });
        push({
          title: 'Success',
          body: `Group renamed to "${values.title}"`,
          bg: 'success',
        });
      },
    });
  };

  // ----- Capability handlers (catalog CRUD) -----
  const capabilityHandlers = useCapabilityHandlers({
    capabilities,
    capabilityId,
    addCut,
    updateCut,
    removeCut,
    addTrim,
    updateTrim,
    removeTrim,
    addExtra,
    updateExtra,
    removeExtra,
    addSpecification,
    updateSpecification,
    removeSpecification,
    updateCapabilitiesInfo,
  });

  // ----- Question handlers (form CRUD) -----
  const questionHandlers = useQuestionHandlers({
    capabilities,
    questionnaire: questionnaire!,
    capabilityId,
    capability: capabilityData?.CutsheetCapabilityById,
    addQuestionAtIndex,
    updateQuestion,
    removeQuestion,
    addItemsToQuestion,
    removeItemFromQuestion,
    reorderItemsInQuestion,
    updateQuestionnaireInfo,
    saveQuestionnaire: async (overrides) => {
      const result = await saveQuestionnaire(overrides);
      // Refetch form list so status chips update
      formListResult.refetch?.();
      return result;
    },
    onArchiveForm: handleArchiveForm,
  });

  // ----- Save handler -----
  // Published forms save directly. Draft/archived forms route through a
  // dialog that prompts the user to publish and add an assignment rule.
  const handleSave = async () => {
    if (!isPublished) {
      setPublishDialogOpen(true);
      return;
    }
    try {
      await saveQuestionnaire();
      formListResult.refetch?.();
      push({ title: 'Saved', body: 'Cut sheet form saved.', bg: 'success' });
    } catch {
      push({ title: 'Error', body: 'Failed to save.', bg: 'error' });
    }
  };

  const handleSaveAsDraft = async () => {
    try {
      await saveQuestionnaire();
      formListResult.refetch?.();
      push({ title: 'Saved', body: 'Cut sheet form saved.', bg: 'success' });
      setPublishDialogOpen(false);
    } catch {
      push({ title: 'Error', body: 'Failed to save.', bg: 'error' });
    }
  };

  const handlePublishWithoutRule = async () => {
    try {
      await saveQuestionnaire({ status: 'published' });
      formListResult.refetch?.();
      setPublishDialogOpen(false);
      push({
        title: 'Published',
        body: 'Cut sheet form published without an assignment rule.',
        bg: 'success',
      });
    } catch {
      push({ title: 'Error', body: 'Failed to publish.', bg: 'error' });
    }
  };

  // Publish the questionnaire and prepend a new assignment rule to the set.
  const handlePublishWithRule = async (rule: Omit<LocalRule, 'uid'>) => {
    try {
      await saveQuestionnaire({ status: 'published' });

      const existingSet =
        assignmentRuleSetData?.cutsheetAssignmentRuleSetByCapabilityId;
      const existingRules = (existingSet?.rules ?? [])
        .filter(Boolean)
        .map((r) => ({
          label: r!.label ?? undefined,
          inspectionLevel: (r!.inspectionLevel ?? []).filter(Boolean),
          splitType: (r!.splitType ?? []).filter(Boolean),
          splitIndex: (r!.splitIndex ?? []).filter(
            (v): v is number => v != null,
          ),
          producer: (r!.producer ?? [])
            .filter(Boolean)
            .map((p: unknown) => String(p)),
          buyer: (r!.buyer ?? [])
            .filter(Boolean)
            .map((b: unknown) => String(b)),
          questionnaireId: String(r!.questionnaireId),
        }));

      const newRule = {
        label: rule.label || undefined,
        inspectionLevel: rule.inspectionLevel.length
          ? rule.inspectionLevel
          : undefined,
        splitType: rule.splitType.length ? rule.splitType : undefined,
        splitIndex: rule.splitIndex.length ? rule.splitIndex : undefined,
        producer: rule.producer.length ? rule.producer : undefined,
        buyer: rule.buyer.length ? rule.buyer : undefined,
        questionnaireId: rule.questionnaireId,
      };

      const combined = [newRule, ...existingRules].map((r, i) => ({
        ...r,
        sortOrder: i,
      }));

      await upsertAssignmentRuleSet({
        variables: {
          capabilityId,
          rules: combined,
          defaultQuestionnaireId:
            existingSet?.defaultQuestionnaireId ?? undefined,
        },
      });

      await Promise.all([
        formListResult.refetch?.(),
        refetchAssignmentRuleSet(),
      ]);

      setPublishDialogOpen(false);
      push({
        title: 'Published',
        body: 'Cut sheet form published with a new assignment rule.',
        bg: 'success',
      });
    } catch {
      push({ title: 'Error', body: 'Failed to publish.', bg: 'error' });
    }
  };

  // ----- Drag handlers (must match data types from DraggableCapabilityItem
  // and QuestionCard droppable zones) -----
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeData = active.data.current;

    // Dragging a capability item (cut/trim/extra/spec)
    if (activeData?.type === 'capability') {
      setIsDraggingCapability(true);
      const item = activeData.capability as Cut | Trim | Extra | Specification;
      const itemType = activeData.capabilityType as
        | 'cut'
        | 'trim'
        | 'extra'
        | 'specification';
      setActiveItem({ item, type: itemType });
    }

    // Dragging a group — no overlay needed, handled by SortableGroupCard transform
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // Reserved for future hover-highlight logic.
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDraggingCapability(false);
    setActiveItem(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Drop a capability onto a question dropzone
    if (
      activeData?.type === 'capability' &&
      overData?.type === 'question-dropzone'
    ) {
      const questionId = (over.id as string).replace('question-dropzone-', '');
      const capability = activeData.capability as
        | Cut
        | Trim
        | Extra
        | Specification;
      if (questionId && capability.id) {
        questionHandlers.handleAddItemToQuestion(questionId, capability.id);
      }
    }

    // Reorder groups via drag-and-drop
    const activeId = active.id as string;
    const overId = over.id as string;
    if (activeId.startsWith('group-') && overId.startsWith('group-')) {
      const oldId = activeId.replace('group-', '');
      const newId = overId.replace('group-', '');
      const ids = capabilities.primals.map((p) => p.id);
      const oldIndex = ids.indexOf(oldId);
      const newIndex = ids.indexOf(newId);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = arrayMove(ids, oldIndex, newIndex);
        reorderPrimals(reordered);
      }
      return;
    }

    // Drop a capability onto a choice group option dropzone
    if (
      activeData?.type === 'capability' &&
      overData?.type === 'option-dropzone'
    ) {
      const { questionId, optionId } = overData as {
        questionId: string;
        optionId: string;
        type: string;
      };
      const capability = activeData.capability as Cut;
      if (questionId && optionId && capability.id) {
        questionHandlers.handleDropCutToOption?.(
          questionId,
          optionId,
          capability.id,
        );
      }
    }
  };

  const customCollisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  };

  // ----- Render -----
  const hasForm = !!selectedFormId && !!questionnaire;

  // Empty questionnaire fallback for CapabilitiesPanel when no form is selected
  const emptyQuestionnaire = useMemo(
    () => ({
      id: '',
      _id: '',
      name: '',
      description: '',
      capabilityId,
      questions: [],
      isPublished: false,
    }),
    [capabilityId],
  ) as any;

  return (
    <Box sx={{ mt: 2 }}>
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Grid container spacing={3}>
          {/* Left Column - Catalog (always visible, always editable) */}
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <CapabilitiesPanel
              capabilities={capabilities}
              questionnaire={questionnaire ?? emptyQuestionnaire}
              isDraggingCapability={isDraggingCapability}
              onAddGroup={handleAddGroup}
              onEditGroup={handleEditGroup}
              onAddCut={capabilityHandlers.handleAddCut}
              onEditCut={capabilityHandlers.handleEditCut}
              onAddTrim={capabilityHandlers.handleAddTrim}
              onEditTrim={capabilityHandlers.handleEditTrim}
              onAddExtra={capabilityHandlers.handleAddExtra}
              onEditExtra={capabilityHandlers.handleEditExtra}
              onAddSpecification={capabilityHandlers.handleAddSpecification}
              onEditSpecification={capabilityHandlers.handleEditSpecification}
            />
          </Grid>

          {/* Right Column - Form builder */}
          <Grid size={{ xs: 12, md: 8 }}>
            {/* Form selector bar
                 - Desktop (md+): single row — selector left, action cluster right.
                 - Mobile (xs):   two rows — selector full-width, actions below.
                 On mobile Preview + Settings collapse into a kebab menu so
                 only the primary actions (New, Save) stay on-screen. */}
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              {/* Always render the dropdown — even with zero forms, the user
                   needs access to the "Include archived" toggle to discover
                   forms that are hidden by default (e.g. after migration). */}
              <Autocomplete
                size="small"
                options={forms}
                getOptionLabel={(form) => form.name}
                disableClearable
                value={selectedForm as GqlCutsheetQuestionnaire}
                onChange={(_e, form) => setSelectedFormId(form?._id ?? null)}
                noOptionsText={
                  includeArchived
                    ? 'No cut sheet forms'
                    : 'No active forms — toggle "Include archived" to see archived forms'
                }
                renderOption={(props, form) => (
                  <li {...props} key={form._id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{form.name}</span>
                      <Chip
                        label={statusLabel(form.status)}
                        size="small"
                        color={statusColor(form.status)}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      forms.length === 0
                        ? 'No cut sheet forms'
                        : 'Select a form...'
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: selectedForm ? (
                        <Chip
                          label={statusLabel(selectedForm.status)}
                          size="small"
                          color={statusColor(selectedForm.status)}
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            ml: 0.5,
                          }}
                        />
                      ) : null,
                    }}
                  />
                )}
                sx={{
                  minWidth: { md: 280 },
                  width: { xs: '100%', md: 'auto' },
                  '& .MuiInputBase-root': { height: 40 },
                }}
                PaperComponent={({ children, ...paperProps }) => (
                  <Paper {...paperProps}>
                    {children}
                    <Divider />
                    <Box
                      sx={{ px: 2, py: 0.5 }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={includeArchived}
                            onChange={(e) =>
                              setIncludeArchived(e.target.checked)
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Include archived
                          </Typography>
                        }
                      />
                    </Box>
                  </Paper>
                )}
              />

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={{ xs: 'space-between', md: 'flex-end' }}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={(e) => setNewMenuAnchor(e.currentTarget)}
                  sx={{ height: 40 }}
                >
                  New
                </Button>
                <Menu
                  anchorEl={newMenuAnchor}
                  open={Boolean(newMenuAnchor)}
                  onClose={() => setNewMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setNewMenuAnchor(null);
                      handleCreateForm();
                    }}
                  >
                    <ListItemIcon>
                      <BlankIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Start from scratch"
                      secondary="Create a blank form"
                    />
                  </MenuItem>
                  {hasForm && questionnaire && (
                    <MenuItem
                      onClick={async () => {
                        setNewMenuAnchor(null);
                        await handleDuplicateForm();
                      }}
                    >
                      <ListItemIcon>
                        <DuplicateIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Duplicate "${questionnaire.name}"`}
                        secondary="Copy the current form"
                      />
                    </MenuItem>
                  )}
                </Menu>
                {hasForm && (
                  <ButtonGroup
                    variant="outlined"
                    sx={{ ml: 'auto', height: 40 }}
                  >
                    <Tooltip title="Preview">
                      <Button
                        onClick={() => setShowPreview(true)}
                        disabled={!questionnaire?.questions?.length}
                      >
                        <PreviewIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Settings">
                      <Button
                        onClick={questionHandlers.handleEditQuestionnaireInfo}
                      >
                        <SettingsIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title={
                        !isPublished
                          ? 'Save (prompts to publish and add a rule)'
                          : isDirty
                            ? 'Save'
                            : 'No changes to save'
                      }
                    >
                      <Button
                        onClick={handleSave}
                        disabled={isSaving || (isPublished && !isDirty)}
                      >
                        <SaveIcon />
                      </Button>
                    </Tooltip>
                  </ButtonGroup>
                )}
              </Stack>
            </Box>

            {/* Form content */}
            {hasForm ? (
              <QuestionnairePanel
                questionnaire={questionnaire}
                questionnaireLoading={questionnaireLoading}
                capabilities={capabilities}
                draggingType={activeItem?.type ?? null}
                onAddQuestion={questionHandlers.handleAddQuestion}
                onEditQuestion={questionHandlers.handleEditQuestion}
                onDeleteQuestion={questionHandlers.handleDeleteQuestion}
                onAddItem={questionHandlers.handleAddItemToQuestion}
                onAddItems={questionHandlers.handleAddItemsToQuestion}
                onRemoveItem={questionHandlers.handleRemoveItemFromQuestion}
                onEditItem={capabilityHandlers.handleEditItemFromQuestion}
                onAddCutToOption={questionHandlers.handleAddCutToOption}
                onRemoveCutFromOption={
                  questionHandlers.handleRemoveCutFromOption
                }
                onReorderQuestions={reorderQuestionsByIndex}
                onReorderItems={questionHandlers.handleReorderItemsInQuestion}
              />
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {forms.length === 0
                    ? 'Create your first cut sheet form'
                    : 'Select a form'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {forms.length === 0
                    ? 'Drag items from the catalog on the left into your form to build the customer experience.'
                    : 'Choose a form from the dropdown above to start editing.'}
                </Typography>
                {forms.length === 0 && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateForm}
                  >
                    Create Cut Sheet Form
                  </Button>
                )}
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Drag overlay */}
        <DragOverlay>
          {activeItem ? (
            <Card
              variant="outlined"
              sx={{
                cursor: 'grabbing',
                border: '2px solid',
                borderColor: 'primary.main',
                boxShadow: 3,
              }}
            >
              <CardHeader
                title={activeItem.item.title || 'Unnamed'}
                titleTypographyProps={{
                  variant: 'body2',
                  fontWeight: 'medium',
                }}
              />
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Preview dialog */}
      {questionnaire && (
        <QuestionnaireWizard
          open={showPreview}
          onClose={() => setShowPreview(false)}
          questionnaire={questionnaire}
          catalog={capabilities}
          printSettings={{
            primalColumns:
              capabilityData?.CutsheetCapabilityById?.printSettings
                ?.primalColumns ?? 2,
            specsLayoutPosition:
              (capabilityData?.CutsheetCapabilityById?.printSettings
                ?.specsLayoutPosition as
                | 'top'
                | 'bottom'
                | 'left'
                | 'right'
                | 'none'
                | undefined) ?? 'right',
            showPrimalGroup:
              capabilityData?.CutsheetCapabilityById?.printSettings
                ?.showPrimalGroup ?? true,
            alwaysShowPrimalNotes:
              capabilityData?.CutsheetCapabilityById?.printSettings
                ?.alwaysShowPrimalNotes ?? true,
            hiddenGroupIds: (
              capabilityData?.CutsheetCapabilityById?.printSettings
                ?.hiddenGroupIds ?? []
            ).filter((id): id is string => Boolean(id)),
            hiddenItemIds: (
              capabilityData?.CutsheetCapabilityById?.printSettings
                ?.hiddenItemIds ?? []
            ).filter((id): id is string => Boolean(id)),
          }}
        />
      )}

      {/* SaveFooter removed — save/preview/settings are inline in the form selector bar */}

      {/* Publish + assignment rule dialog (triggered when saving a draft) */}
      {questionnaire && (
        <RuleDialog
          open={publishDialogOpen}
          initial={{
            ...EMPTY_RULE,
            questionnaireId: questionnaire.id,
          }}
          questionnaires={forms.map((f) => ({
            _id: f._id as string,
            name: f.name,
          }))}
          vendorId={vendor?._id}
          title="Publish cut sheet form"
          description={`"${questionnaire.name}" is a draft. Configure an assignment rule so incoming orders can be routed to it, then publish.`}
          submitLabel="Publish & Add Rule"
          lockQuestionnaire
          secondaryLabel="Save as Draft"
          onSecondary={handleSaveAsDraft}
          tertiaryLabel="Publish without Rule"
          onTertiary={handlePublishWithoutRule}
          onSave={handlePublishWithRule}
          onClose={() => setPublishDialogOpen(false)}
        />
      )}
    </Box>
  );
};
